import { categorizeRepositoriesByVisibility, 
    groupScopesByAction, categorizeRepositoriesByOwner, 
    groupScopesByInScope } from './github';
import { groupSlackByScopeCategory, groupSlackByPermissionType, 
    groupSlackByResourceType, 
    groupSlackByRiskLevel, 
    groupSlackByContext 
  } from './slack';
import { getHuggingFaceScopeStats, 
    getScopeDisplayLabel, getScopeColor 
  } from './huggingface';
import { createSendGridPermissionItems,
    getScopeColor as getSendGridScopeColor,
    getSubScopeDisplayLabel, getTotalPermissions, groupSendGridByFunctionalDomain,
    getFunctionalDomainColor, getFunctionalDomainStats, getDomainDisplayLabel
  } from './sendgrid';
import {toSlug, placeChildrenTree} from './helpers';
import { groupOpenAIScopesIntoCategories, getPermissionColor } from './openai';
import { 
  getWorkspaceTypeColor, 
  getWorkspaceVisibilityColor, 
  getRoleColor,
  groupWorkspacesByType,
  groupWorkspacesByVisibility,
  groupRolesByScope,
  createWorkspaceItems,
  createRoleItems
} from './postman';

export function buildNodeData(expandedNodes, normalized, insightData) {
    if (!insightData) return {};

    const allRepos = normalized.repositories || [];
    // const visibilityGroups = categorizeRepositoriesByVisibility(allRepos);
    // const ownerGroups = categorizeRepositoriesByOwner(allRepos);
    const scopeInScope = groupScopesByInScope(normalized.scopes || []);
    const scopeActions = groupScopesByAction((normalized.scopes || []).map(s => s.scope));

    const nodes = {
      'github-token': {
        id: 'github-token',
        label: `${normalized.provider} Token\n${normalized.token_user || 'Unknown'}`,
        x: 400,
        y: 300,
        color: '#3b82f6',
        connections: (normalized.provider || '').toLowerCase() === 'slack' ? [] : ['token-info']
      },
    };

    // Only add token-info node for non-Slack providers
    if ((normalized.provider || '').toLowerCase() !== 'slack') {
      let tokenInfoLabel = 'Token Info';
      if ((normalized.provider || '').toLowerCase() === 'aws') {
        // For AWS, show access key info
        const accessKeyId = normalized.access_key_info?.access_key_id || 'Unknown';
        const createdAt = normalized.access_key_info?.created_at ? new Date(normalized.access_key_info.created_at).toLocaleDateString() : 'N/A';
        const lastUsed = normalized.access_key_info?.last_used ? new Date(normalized.access_key_info.last_used).toLocaleDateString() : 'Never';
        tokenInfoLabel = `Access Key Info\nID: ${accessKeyId.substring(0, 12)}...\nCreated: ${createdAt}\nLast Used: ${lastUsed}`;
      } else {
        tokenInfoLabel = `Token Info\n${normalized.token_type ? `type: ${normalized.token_type}` : ''}${normalized.token_type && (normalized.token_expiration || normalized.days_remaining != null) ? '\n' : ''}${normalized.token_expiration ? `expires: ${normalized.token_expiration}` : ''}${(normalized.token_expiration && normalized.days_remaining != null) ? '\n' : ''}${normalized.days_remaining != null ? `${normalized.days_remaining} days left` : ''}`;
      }
      
      nodes['token-info'] = {
        id: 'token-info',
        label: tokenInfoLabel,
        x: 200,
        y: 150,
        color: '#f59e0b',
        connections: []
      };
    }

    const MAX_CHILDREN = 8;

    // Add Repositories group if present (GitHub) → bifurcation retained
    if (allRepos.length > 0) {
      const visibilityGroups = categorizeRepositoriesByVisibility(allRepos);
      const ownerGroups = categorizeRepositoriesByOwner(allRepos);

      nodes['repositories'] = {
        id: 'repositories',
        label: `Repositories\n${normalized.total_repositories || allRepos.length} total`,
        x: 600,
        y: 200,
        color: '#10b981',
        connections: ['repo-visibility', 'repo-ownership']
      };
      nodes['github-token'].connections.push('repositories');

      // Visibility group
      const publicCount = visibilityGroups.public.length;
      const privateCount = visibilityGroups.private.length;
      nodes['repo-visibility'] = {
        id: 'repo-visibility',
        label: `By Visibility`,
        x: 760,
        y: 120,
        color: '#22d3ee',
        connections: ['repo-public', 'repo-private']
      };

      const publicListId = 'repo-public-list';
      nodes['repo-public'] = {
        id: 'repo-public',
        label: `Public\n${publicCount} repos`,
        x: 870,
        y: 40,
        color: '#0ea5e9',
        connections: [publicListId]
      };

      const privateListId = 'repo-private-list';
      nodes['repo-private'] = {
        id: 'repo-private',
        label: `Private\n${privateCount} repos`,
        x: 870,
        y: 200,
        color: '#0891b2',
        connections: [privateListId]
      };

      // On expand of visibility buckets, show a single unified list node containing all repos
      if (expandedNodes.has('repo-public') && publicCount > 0) {
        const listId = publicListId;
        const items = visibilityGroups.public.map(r => ({ name: r.name || r.id || 'unknown', url: r.url || r.html_url || '' }));
        const listWidth = 520;
        const lineHeight = 16;
        const padding = 24;
        const listHeight = padding + lineHeight * Math.max(1, Math.ceil(items.length / 2)) * 2; // approximate for 2 cols
        const pos = { x: nodes['repo-public'].x + 180, y: nodes['repo-public'].y + 150 };
        nodes[listId] = { id: listId, label: `Public Repositories`, items, x: pos.x, y: pos.y, color: '#93c5fd', connections: [], width: listWidth, height: listHeight };
      }

      if (expandedNodes.has('repo-private') && privateCount > 0) {
        const listId = privateListId;
        const items = visibilityGroups.private.map(r => ({ name: r.name || r.id || 'unknown', url: r.url || r.html_url || '' }));
        const listWidth = 520;
        const lineHeight = 16;
        const padding = 24;
        const listHeight = padding + lineHeight * Math.max(1, Math.ceil(items.length / 2)) * 2;
        const pos = { x: nodes['repo-private'].x + 180, y: nodes['repo-private'].y + 150 };
        nodes[listId] = { id: listId, label: `Private Repositories`, items, x: pos.x, y: pos.y, color: '#fde68a', connections: [], width: listWidth, height: listHeight };
      }

      // Ownership group
      const ownerKeys = Object.keys(ownerGroups);
      nodes['repo-ownership'] = {
        id: 'repo-ownership',
        label: `By Ownership`,
        x: 760,
        y: 280,
        color: '#34d399',
        connections: ownerKeys.map((o) => `owner-${toSlug(o)}`)
      };

      let ownerYOffset = 360;
      ownerKeys.forEach((owner) => {
        const reposForOwner = ownerGroups[owner];
        const ownerId = `owner-${toSlug(owner)}`;
        const ownerListId = `${ownerId}-list`;
        nodes[ownerId] = {
          id: ownerId,
          label: `${owner}\n${reposForOwner.length} repos`,
          x: 870,
          y: ownerYOffset,
          color: '#a7f3d0',
          connections: [ownerListId],
        };
        ownerYOffset += 80;

        if (expandedNodes.has(ownerId)) {
          const listId = ownerListId;
          const items = reposForOwner.map(r => ({ name: r.name || r.id || 'unknown', url: r.url || r.html_url || '' }));
          const listWidth = 520;
          const lineHeight = 16;
          const padding = 24;
          const listHeight = padding + lineHeight * Math.max(1, Math.ceil(items.length / 2)) * 2;
          const pos = { x: nodes[ownerId].x + 180, y: nodes[ownerId].y + 150 };
          nodes[listId] = { id: listId, label: `${owner} Repositories`, items, x: pos.x, y: pos.y, color: '#d1fae5', connections: [], width: listWidth, height: listHeight };
        }
      });
    }

    // Add Scopes group if present (GitHub/SendGrid/Slack/OpenAI) → provider-specific logic
    if ((normalized.scopes || []).length > 0) {
      // Check if this is SendGrid data
      const providerLower = (normalized.provider || '').toLowerCase();
      const isSendGrid = providerLower === 'sendgrid';
      const isOpenAI = providerLower === 'openai';
      
      if (isSendGrid) {
        // SendGrid functional domain organization
        const domainGroups = groupSendGridByFunctionalDomain(normalized.scopes);
        const domainStats = getFunctionalDomainStats(normalized.scopes);
        const totalPermissions = getTotalPermissions(normalized.scopes);
        const totalScopes = normalized.scopes.length;

        // Main SendGrid node with key info
        nodes['scopes'] = {
          id: 'scopes',
          label: `SendGrid API Key\n${normalized.key_type || 'Full Access'}\n${totalScopes} scopes • ${totalPermissions} permissions`,
          x: 200,
          y: 450,
          color: '#8b5cf6',
          connections: Object.keys(domainGroups).filter(domain => domainStats[domain].scopeCount > 0).map(domain => `sg-domain-${toSlug(domain)}`)
        };
        nodes['github-token'].connections.push('scopes');

        // Add 2FA indicator if required
        if (normalized.scopes.some(scope => scope['2fa_required'])) {
          nodes['sg-2fa'] = {
            id: 'sg-2fa',
            label: `2FA Required\nSecurity Feature`,
            x: 50,
            y: 350,
            color: '#dc2626',
            connections: []
          };
          nodes['scopes'].connections.push('sg-2fa');
        }

        // Create functional domain nodes
        let domainYOffset = 600;
        Object.entries(domainGroups).forEach(([domain, scopes]) => {
          if (scopes.length === 0) return; // Skip empty domains
          
          const domainId = `sg-domain-${toSlug(domain)}`;
          const stats = domainStats[domain];
          
          // Group scopes by main scope within this domain
          const scopeGroups = {};
          scopes.forEach(scope => {
            const scopeName = scope.scope;
            if (!scopeGroups[scopeName]) {
              scopeGroups[scopeName] = [];
            }
            scopeGroups[scopeName].push(scope);
          });
          
          nodes[domainId] = {
            id: domainId,
            label: `${getDomainDisplayLabel(domain)}\n${stats.scopeCount} scopes • ${stats.permissionCount} permissions`,
            x: 50,
            y: domainYOffset,
            color: getFunctionalDomainColor(domain),
            connections: Object.keys(scopeGroups).map(scopeName => `sg-scope-${toSlug(domain)}-${toSlug(scopeName)}`)
          };
          domainYOffset += 100;

          // Create scope nodes within each domain
          let scopeYOffset = domainYOffset;
          Object.entries(scopeGroups).forEach(([scopeName, scopeList]) => {
            const scopeId = `sg-scope-${toSlug(domain)}-${toSlug(scopeName)}`;
            
            // Group by sub-scope
            const subScopeMap = {};
            scopeList.forEach(scope => {
              const subScope = scope.sub_scope || 'General';
              if (!subScopeMap[subScope]) {
                subScopeMap[subScope] = [];
              }
              subScopeMap[subScope].push(scope);
            });
            
            const subScopeKeys = Object.keys(subScopeMap);
            const hasSubScopes = subScopeKeys.length > 1 || (subScopeKeys.length === 1 && subScopeKeys[0] !== 'General');
            
            nodes[scopeId] = {
              id: scopeId,
              label: `${scopeName}\n${scopeList.length} scopes${hasSubScopes ? ` • ${subScopeKeys.length} sub-scopes` : ''}`,
              x: 200,
              y: scopeYOffset,
              color: getSendGridScopeColor(scopeName),
              connections: hasSubScopes ? subScopeKeys.map(subScope => `sg-subscope-${toSlug(domain)}-${toSlug(scopeName)}-${toSlug(subScope)}`) : [`sg-permissions-${toSlug(domain)}-${toSlug(scopeName)}`]
            };
            scopeYOffset += 80;

            if (hasSubScopes) {
              // Create sub-scope nodes
              let subScopeYOffset = scopeYOffset;
              subScopeKeys.forEach(subScopeName => {
                const subScopeId = `sg-subscope-${toSlug(domain)}-${toSlug(scopeName)}-${toSlug(subScopeName)}`;
                const subScopeScopes = subScopeMap[subScopeName] || [];
                const permissionId = `sg-permissions-${toSlug(domain)}-${toSlug(scopeName)}-${toSlug(subScopeName)}`;
                
                nodes[subScopeId] = {
                  id: subScopeId,
                  label: `${getSubScopeDisplayLabel(subScopeName)}\n${subScopeScopes.length} scopes`,
                  x: 350,
                  y: subScopeYOffset,
                  color: getSendGridScopeColor(scopeName),
                  connections: subScopeScopes.length > 0 ? [permissionId] : []
                };
                subScopeYOffset += 60;

                // Create permission list for sub-scope when expanded
                if (expandedNodes.has(subScopeId) && subScopeScopes.length > 0) {
                  const allPermissions = subScopeScopes.flatMap(scope => scope.permissions || []);
                  const items = createSendGridPermissionItems(allPermissions);
                  const listWidth = 520;
                  const lineHeight = 16;
                  const padding = 24;
                  const listHeight = padding + lineHeight * Math.max(1, Math.ceil(items.length / 2)) * 2;
                  const pos = { x: nodes[subScopeId].x + 180, y: nodes[subScopeId].y + 150 };
                  
                  nodes[permissionId] = { 
                    id: permissionId, 
                    label: `${getSubScopeDisplayLabel(subScopeName)} Permissions`, 
                    items, 
                    x: pos.x, 
                    y: pos.y, 
                    color: getSendGridScopeColor(scopeName), 
                    connections: [], 
                    width: listWidth, 
                    height: listHeight 
                  };
                }
              });
            } else {
              // Create direct permission list for scopes without sub-scopes
              if (expandedNodes.has(scopeId) && scopeList.length > 0) {
                const allPermissions = scopeList.flatMap(scope => scope.permissions || []);
                const items = createSendGridPermissionItems(allPermissions);
                const listWidth = 520;
                const lineHeight = 16;
                const padding = 24;
                const listHeight = padding + lineHeight * Math.max(1, Math.ceil(items.length / 2)) * 2;
                const pos = { x: nodes[scopeId].x + 180, y: nodes[scopeId].y + 150 };
                
                nodes[`sg-permissions-${toSlug(domain)}-${toSlug(scopeName)}`] = { 
                  id: `sg-permissions-${toSlug(domain)}-${toSlug(scopeName)}`, 
                  label: `${scopeName} Permissions`, 
                  items, 
                  x: pos.x, 
                  y: pos.y, 
                  color: getSendGridScopeColor(scopeName), 
                  connections: [], 
                  width: listWidth, 
                  height: listHeight 
                };
              }
            }
          });
        });

      } else if (isOpenAI) {
        // OpenAI: build category clusters with endpoint list nodes
        const categories = groupOpenAIScopesIntoCategories(normalized.scopes || []);
        const totalScopes = normalized.total_scopes || (normalized.scopes || []).length;

        nodes['scopes'] = {
          id: 'scopes',
          // eslint-disable-next-line
          label: `OpenAI Key Permissions\n${totalScopes} scopes` + `${(insightData?.insights_json_dump?.is_restricted || insightData?.is_restricted) ? '\nRestricted' : ''}`,
          x: 200,
          y: 450,
          color: '#8b5cf6',
          connections: Object.keys(categories).map((c) => `oa-cat-${toSlug(c)}`)
        };
        nodes['github-token'].connections.push('scopes');

        let yOffset = 540;
        Object.entries(categories).forEach(([cat, entries], idx) => {
          const catId = `oa-cat-${toSlug(cat)}`;
          const catColor = ['#10b981', '#f59e0b', '#3b82f6', '#22d3ee', '#ef4444'][idx % 5];
          const listId = `${catId}-list`;
          nodes[catId] = {
            id: catId,
            label: `${cat}\n${entries.length} scopes`,
            x: 80 + (idx % 2) * 220,
            y: yOffset + Math.floor(idx / 2) * 120,
            color: catColor,
            connections: entries.length > 0 ? [listId] : []
          };

          if (expandedNodes.has(catId) && entries.length > 0) {
            const items = entries.flatMap((e) => {
              const perm = e.permission;
              const color = getPermissionColor(perm);
              const endpoints = (e.endpoints && e.endpoints.length > 0) ? e.endpoints : [e.scope];
              return endpoints.map((ep) => ({ name: `${ep} — ${perm}`, color }));
            });
            const listWidth = 600;
            const lineHeight = 18;
            const padding = 24;
            const listHeight = padding + lineHeight * Math.max(1, Math.ceil(items.length / 2)) * 2;
            const pos = { x: nodes[catId].x + 180, y: nodes[catId].y + 150 };
            nodes[listId] = { id: listId, label: `${cat} Endpoints`, items, x: pos.x, y: pos.y, color: catColor, connections: [], width: listWidth, height: listHeight };
          }
        });

      } else {
        // Original GitHub/Slack scope logic
        nodes['scopes'] = {
          id: 'scopes',
          label: `Permissions\n${normalized.total_scopes || (normalized.scopes || []).length} scopes`,
          x: 200,
          y: 450,
          color: '#8b5cf6',
          connections: ['scopes-in-scope', 'scopes-actions']
        };
        nodes['github-token'].connections.push('scopes');

        // In-Scope grouping
        const trueCount = scopeInScope.true.length;
        const falseCount = scopeInScope.false.length;
      nodes['scopes-in-scope'] = {
        id: 'scopes-in-scope',
        label: `By In-Scope`,
        x: 60,
        y: 520,
        color: '#f472b6',
        connections: ['scopes-in-scope-true', 'scopes-in-scope-false']
      };

      nodes['scopes-in-scope-true'] = {
        id: 'scopes-in-scope-true',
        label: `In Scope: true\n${trueCount}`,
        x: -40,
        y: 600,
        color: '#22c55e',
        connections: Array.from({ length: Math.min(trueCount, MAX_CHILDREN) }).map((_, i) => `inscope-true-${toSlug(scopeInScope.true[i]?.scope || String(i))}`)
      };

      nodes['scopes-in-scope-false'] = {
        id: 'scopes-in-scope-false',
        label: `In Scope: false\n${falseCount}`,
        x: -40,
        y: 700,
        color: '#ef4444',
        connections: Array.from({ length: Math.min(falseCount, MAX_CHILDREN) }).map((_, i) => `inscope-false-${toSlug(scopeInScope.false[i]?.scope || String(i))}`)
      };

      if (expandedNodes.has('scopes-in-scope-true') && trueCount > 0) {
        const total = trueCount;
        const toShow = Math.min(total, MAX_CHILDREN);
        const positions = placeChildrenTree(nodes['scopes-in-scope-true'].x, nodes['scopes-in-scope-true'].y, toShow + (total > toShow ? 1 : 0), 180, 150);
        for (let i = 0; i < toShow; i++) {
          const s = scopeInScope.true[i];
          const id = `inscope-true-${toSlug(s.scope)}`;
          nodes[id] = { id, label: s.scope, x: positions[i].x, y: positions[i].y, color: '#bbf7d0', connections: [] };
          nodes['scopes-in-scope-true'].connections.push(id);
        }
        if (total > toShow) {
          const remainingIds = scopeInScope.true.slice(toShow).map(s => `inscope-true-${toSlug(s.scope)}`);
          const moreId = 'inscope-true-more';
          nodes[moreId] = { id: moreId, label: `+${total - toShow} more`, x: positions[toShow].x, y: positions[toShow].y, color: '#60a5fa', connections: remainingIds };
          nodes['scopes-in-scope-true'].connections.push(moreId);
        }
      }

      if (expandedNodes.has('scopes-in-scope-false') && falseCount > 0) {
        const total = falseCount;
        const toShow = Math.min(total, MAX_CHILDREN);
        const positions = placeChildrenTree(nodes['scopes-in-scope-false'].x, nodes['scopes-in-scope-false'].y, toShow + (total > toShow ? 1 : 0), 180, 150);
        for (let i = 0; i < toShow; i++) {
          const s = scopeInScope.false[i];
          const id = `inscope-false-${toSlug(s.scope)}`;
          nodes[id] = { id, label: s.scope, x: positions[i].x, y: positions[i].y, color: '#fecaca', connections: [] };
          nodes['scopes-in-scope-false'].connections.push(id);
        }
        if (total > toShow) {
          const remainingIds = scopeInScope.false.slice(toShow).map(s => `inscope-false-${toSlug(s.scope)}`);
          const moreId = 'inscope-false-more';
          nodes[moreId] = { id: moreId, label: `+${total - toShow} more`, x: positions[toShow].x, y: positions[toShow].y, color: '#60a5fa', connections: remainingIds };
          nodes['scopes-in-scope-false'].connections.push(moreId);
        }
      }

      // Actions grouping
      nodes['scopes-actions'] = {
        id: 'scopes-actions',
        label: `By Action`,
        x: 200,
        y: 620,
        color: '#f59e0b',
        connections: Object.keys(scopeActions).map(a => `scope-action-${toSlug(a)}`)
      };

      let actionYOffset = 700;
      Object.entries(scopeActions).forEach(([action, resources]) => {
        const actionId = `scope-action-${toSlug(action)}`;
        const maxChildren = Math.min(resources.length, MAX_CHILDREN);
        const potentialChildIds = Array.from({ length: maxChildren }).map((_, i) => `${actionId}-${toSlug(resources[i])}`);
        nodes[actionId] = {
          id: actionId,
          label: `${action}\n${resources.length}`,
          x: 280,
          y: actionYOffset,
          color: '#fde68a',
          connections: potentialChildIds,
        };
        actionYOffset += 80;

        if (expandedNodes.has(actionId)) {
          const total = resources.length;
          const toShow = Math.min(total, MAX_CHILDREN);
          const positions = placeChildrenTree(nodes[actionId].x, nodes[actionId].y, toShow + (total > toShow ? 1 : 0), 180, 150);
          for (let i = 0; i < toShow; i++) {
            const res = resources[i];
            const childId = `${actionId}-${toSlug(res)}`;
            nodes[childId] = { id: childId, label: res, x: positions[i].x, y: positions[i].y, color: '#ffedd5', connections: [] };
            nodes[actionId].connections.push(childId);
          }
          if (total > toShow) {
            const remainingIds = resources.slice(toShow).map(r => `${actionId}-${toSlug(r)}`);
            const moreId = `${actionId}-more`;
            nodes[moreId] = { id: moreId, label: `+${total - toShow} more`, x: positions[toShow].x, y: positions[toShow].y, color: '#60a5fa', connections: remainingIds };
            nodes[actionId].connections.push(moreId);
          }
        }
      });

      // Slack-specific groupings (5 views) when provider is Slack
      if ((normalized.provider || '').toLowerCase() === 'slack') {
        const slackScopes = (normalized.scopes || []).map(s => s.scope);

        // 1) By Scope Category
        const byScopeCategory = groupSlackByScopeCategory(slackScopes);
        nodes['slack-by-category'] = {
          id: 'slack-by-category',
          label: 'By Scope Category',
          x: 20,
          y: 760,
          color: '#60a5fa',
          connections: Object.keys(byScopeCategory).map(k => `slack-cat-${toSlug(k)}`)
        };
        nodes['scopes'].connections.push('slack-by-category');
        let catYOffset = 840;
        Object.entries(byScopeCategory).forEach(([cat, list]) => {
          const catId = `slack-cat-${toSlug(cat)}`;
          nodes[catId] = {
            id: catId,
            label: `${cat}\n${list.length}`,
            x: 100,
            y: catYOffset,
            color: '#bfdbfe',
            connections: Array.from({ length: Math.min(list.length, MAX_CHILDREN) }).map((_, i) => `${catId}-${toSlug(list[i])}`)
          };
          catYOffset += 80;
          if (expandedNodes.has(catId) && list.length > 0) {
            const total = list.length;
            const toShow = Math.min(total, MAX_CHILDREN);
            const positions = placeChildrenTree(nodes[catId].x, nodes[catId].y, toShow + (total > toShow ? 1 : 0), 180, 150);
            for (let i = 0; i < toShow; i++) {
              const s = list[i];
              const cid = `${catId}-${toSlug(s)}`;
              nodes[cid] = { id: cid, label: s, x: positions[i].x, y: positions[i].y, color: '#e0f2fe', connections: [] };
              nodes[catId].connections.push(cid);
            }
            if (total > toShow) {
              const remainingIds = list.slice(toShow).map(s => `${catId}-${toSlug(s)}`);
              const moreId = `${catId}-more`;
              nodes[moreId] = { id: moreId, label: `+${total - toShow} more`, x: positions[toShow].x, y: positions[toShow].y, color: '#60a5fa', connections: remainingIds };
              nodes[catId].connections.push(moreId);
            }
          }
        });

        // 2) By Permission Type
        const byPermType = groupSlackByPermissionType(slackScopes);
        nodes['slack-by-permission'] = {
          id: 'slack-by-permission',
          label: 'By Permission Type',
          x: 220,
          y: 760,
          color: '#f59e0b',
          connections: Object.keys(byPermType).map(k => `slack-perm-${toSlug(k)}`)
        };
        nodes['scopes'].connections.push('slack-by-permission');
        let permYOffset = 840;
        Object.entries(byPermType).forEach(([perm, list]) => {
          const permId = `slack-perm-${toSlug(perm)}`;
          nodes[permId] = {
            id: permId,
            label: `${perm}\n${list.length}`,
            x: 300,
            y: permYOffset,
            color: '#fde68a',
            connections: Array.from({ length: Math.min(list.length, MAX_CHILDREN) }).map((_, i) => `${permId}-${toSlug(list[i])}`)
          };
          permYOffset += 80;
          if (expandedNodes.has(permId) && list.length > 0) {
            const total = list.length;
            const toShow = Math.min(total, MAX_CHILDREN);
            const positions = placeChildrenTree(nodes[permId].x, nodes[permId].y, toShow + (total > toShow ? 1 : 0), 180, 150);
            for (let i = 0; i < toShow; i++) {
              const s = list[i];
              const cid = `${permId}-${toSlug(s)}`;
              nodes[cid] = { id: cid, label: s, x: positions[i].x, y: positions[i].y, color: '#ffedd5', connections: [] };
              nodes[permId].connections.push(cid);
            }
            if (total > toShow) {
              const remainingIds = list.slice(toShow).map(s => `${permId}-${toSlug(s)}`);
              const moreId = `${permId}-more`;
              nodes[moreId] = { id: moreId, label: `+${total - toShow} more`, x: positions[toShow].x, y: positions[toShow].y, color: '#60a5fa', connections: remainingIds };
              nodes[permId].connections.push(moreId);
            }
          }
        });

        // 3) By Resource Type
        const byResource = groupSlackByResourceType(slackScopes);
        nodes['slack-by-resource'] = {
          id: 'slack-by-resource',
          label: 'By Resource Type',
          x: 420,
          y: 760,
          color: '#10b981',
          connections: Object.keys(byResource).map(k => `slack-res-${toSlug(k)}`)
        };
        nodes['scopes'].connections.push('slack-by-resource');
        let resYOffset = 840;
        Object.entries(byResource).forEach(([res, list]) => {
          const resId = `slack-res-${toSlug(res)}`;
          nodes[resId] = {
            id: resId,
            label: `${res}\n${list.length}`,
            x: 500,
            y: resYOffset,
            color: '#a7f3d0',
            connections: Array.from({ length: Math.min(list.length, MAX_CHILDREN) }).map((_, i) => `${resId}-${toSlug(list[i])}`)
          };
          resYOffset += 80;
          if (expandedNodes.has(resId) && list.length > 0) {
            const total = list.length;
            const toShow = Math.min(total, MAX_CHILDREN);
            const positions = placeChildrenTree(nodes[resId].x, nodes[resId].y, toShow + (total > toShow ? 1 : 0), 180, 150);
            for (let i = 0; i < toShow; i++) {
              const s = list[i];
              const cid = `${resId}-${toSlug(s)}`;
              nodes[cid] = { id: cid, label: s, x: positions[i].x, y: positions[i].y, color: '#d1fae5', connections: [] };
              nodes[resId].connections.push(cid);
            }
            if (total > toShow) {
              const remainingIds = list.slice(toShow).map(s => `${resId}-${toSlug(s)}`);
              const moreId = `${resId}-more`;
              nodes[moreId] = { id: moreId, label: `+${total - toShow} more`, x: positions[toShow].x, y: positions[toShow].y, color: '#60a5fa', connections: remainingIds };
              nodes[resId].connections.push(moreId);
            }
          }
        });

        // 4) By Risk Level
        const byRisk = groupSlackByRiskLevel(slackScopes);
        nodes['slack-by-risk'] = {
          id: 'slack-by-risk',
          label: 'By Risk Level',
          x: 620,
          y: 760,
          color: '#ef4444',
          connections: Object.keys(byRisk).map(k => `slack-risk-${toSlug(k)}`)
        };
        nodes['scopes'].connections.push('slack-by-risk');
        let riskYOffset = 840;
        Object.entries(byRisk).forEach(([risk, list]) => {
          const riskId = `slack-risk-${toSlug(risk)}`;
          nodes[riskId] = {
            id: riskId,
            label: `${risk}\n${list.length}`,
            x: 700,
            y: riskYOffset,
            color: '#fecaca',
            connections: Array.from({ length: Math.min(list.length, MAX_CHILDREN) }).map((_, i) => `${riskId}-${toSlug(list[i])}`)
          };
          riskYOffset += 80;
          if (expandedNodes.has(riskId) && list.length > 0) {
            const total = list.length;
            const toShow = Math.min(total, MAX_CHILDREN);
            const positions = placeChildrenTree(nodes[riskId].x, nodes[riskId].y, toShow + (total > toShow ? 1 : 0), 180, 150);
            for (let i = 0; i < toShow; i++) {
              const s = list[i];
              const cid = `${riskId}-${toSlug(s)}`;
              nodes[cid] = { id: cid, label: s, x: positions[i].x, y: positions[i].y, color: '#ffe4e6', connections: [] };
              nodes[riskId].connections.push(cid);
            }
            if (total > toShow) {
              const remainingIds = list.slice(toShow).map(s => `${riskId}-${toSlug(s)}`);
              const moreId = `${riskId}-more`;
              nodes[moreId] = { id: moreId, label: `+${total - toShow} more`, x: positions[toShow].x, y: positions[toShow].y, color: '#60a5fa', connections: remainingIds };
              nodes[riskId].connections.push(moreId);
            }
          }
        });

        // 5) By User / Team Context
        const byContext = groupSlackByContext(slackScopes);
        nodes['slack-by-context'] = {
          id: 'slack-by-context',
          label: 'By Context',
          x: 820,
          y: 760,
          color: '#6366f1',
          connections: Object.keys(byContext).map(k => `slack-context-${toSlug(k)}`)
        };
        nodes['scopes'].connections.push('slack-by-context');
        let ctxYOffset = 840;
        Object.entries(byContext).forEach(([ctx, list]) => {
          const ctxId = `slack-context-${toSlug(ctx)}`;
          nodes[ctxId] = {
            id: ctxId,
            label: `${ctx}\n${list.length}`,
            x: 900,
            y: ctxYOffset,
            color: '#c7d2fe',
            connections: Array.from({ length: Math.min(list.length, MAX_CHILDREN) }).map((_, i) => `${ctxId}-${toSlug(list[i])}`)
          };
          ctxYOffset += 80;
          if (expandedNodes.has(ctxId) && list.length > 0) {
            const total = list.length;
            const toShow = Math.min(total, MAX_CHILDREN);
            const positions = placeChildrenTree(nodes[ctxId].x, nodes[ctxId].y, toShow + (total > toShow ? 1 : 0), 180, 150);
            for (let i = 0; i < toShow; i++) {
              const s = list[i];
              const cid = `${ctxId}-${toSlug(s)}`;
              nodes[cid] = { id: cid, label: s, x: positions[i].x, y: positions[i].y, color: '#e0e7ff', connections: [] };
              nodes[ctxId].connections.push(cid);
            }
            if (total > toShow) {
              const remainingIds = list.slice(toShow).map(s => `${ctxId}-${toSlug(s)}`);
              const moreId = `${ctxId}-more`;
              nodes[moreId] = { id: moreId, label: `+${total - toShow} more`, x: positions[toShow].x, y: positions[toShow].y, color: '#60a5fa', connections: remainingIds };
              nodes[ctxId].connections.push(moreId);
            }
          }
        });
      }
      }
    }

    // Add Postman-specific groups if present
    if ((normalized.provider || '').toLowerCase() === 'postman') {
      const workspaces = normalized.workspaces || [];
      const roles = normalized.roles || [];
      const teamInfo = normalized.team_info || {};
      const userInfo = normalized.user_info || {};

      // Main Postman API Key node
      nodes['github-token'] = {
        id: 'github-token',
        label: `Postman API Key\n${userInfo.email || userInfo.username || 'Unknown User'}`,
        x: 400,
        y: 300,
        color: '#ff6b35',
        connections: ['token-info', 'workspaces', 'roles']
      };

      // Token info node
      nodes['token-info'] = {
        id: 'token-info',
        label: `Token Info\nValid: ${normalized.valid_key ? 'Yes' : 'No'}\nTeam: ${teamInfo.name || 'N/A'}`,
        x: 200,
        y: 150,
        color: '#f59e0b',
        connections: []
      };

      // Workspaces group
      if (workspaces.length > 0) {
        const typeGroups = groupWorkspacesByType(workspaces);
        const visibilityGroups = groupWorkspacesByVisibility(workspaces);

        nodes['workspaces'] = {
          id: 'workspaces',
          label: `Workspaces\n${workspaces.length} total`,
          x: 600,
          y: 200,
          color: '#10b981',
          connections: ['workspace-type', 'workspace-visibility']
        };

        // Workspace type grouping
        nodes['workspace-type'] = {
          id: 'workspace-type',
          label: 'By Type',
          x: 760,
          y: 120,
          color: '#22d3ee',
          connections: Object.keys(typeGroups).map(type => `workspace-type-${toSlug(type)}`)
        };

        let typeYOffset = 200;
        Object.entries(typeGroups).forEach(([type, typeWorkspaces]) => {
          const typeId = `workspace-type-${toSlug(type)}`;
          const typeListId = `${typeId}-list`;
          
          nodes[typeId] = {
            id: typeId,
            label: `${type}\n${typeWorkspaces.length} workspaces`,
            x: 870,
            y: typeYOffset,
            color: getWorkspaceTypeColor(type),
            connections: [typeListId]
          };
          typeYOffset += 80;

          if (expandedNodes.has(typeId) && typeWorkspaces.length > 0) {
            const items = createWorkspaceItems(typeWorkspaces);
            const listWidth = 520;
            const lineHeight = 16;
            const padding = 24;
            const listHeight = padding + lineHeight * Math.max(1, Math.ceil(items.length / 2)) * 2;
            const pos = { x: nodes[typeId].x + 180, y: nodes[typeId].y + 150 };
            
            nodes[typeListId] = { 
              id: typeListId, 
              label: `${type} Workspaces`, 
              items, 
              x: pos.x, 
              y: pos.y, 
              color: getWorkspaceTypeColor(type), 
              connections: [], 
              width: listWidth, 
              height: listHeight 
            };
          }
        });

        // Workspace visibility grouping
        nodes['workspace-visibility'] = {
          id: 'workspace-visibility',
          label: 'By Visibility',
          x: 760,
          y: 400,
          color: '#34d399',
          connections: Object.keys(visibilityGroups).map(visibility => `workspace-visibility-${toSlug(visibility)}`)
        };

        let visibilityYOffset = 480;
        Object.entries(visibilityGroups).forEach(([visibility, visibilityWorkspaces]) => {
          const visibilityId = `workspace-visibility-${toSlug(visibility)}`;
          const visibilityListId = `${visibilityId}-list`;
          
          nodes[visibilityId] = {
            id: visibilityId,
            label: `${visibility}\n${visibilityWorkspaces.length} workspaces`,
            x: 870,
            y: visibilityYOffset,
            color: getWorkspaceVisibilityColor(visibility),
            connections: [visibilityListId]
          };
          visibilityYOffset += 80;

          if (expandedNodes.has(visibilityId) && visibilityWorkspaces.length > 0) {
            const items = createWorkspaceItems(visibilityWorkspaces);
            const listWidth = 520;
            const lineHeight = 16;
            const padding = 24;
            const listHeight = padding + lineHeight * Math.max(1, Math.ceil(items.length / 2)) * 2;
            const pos = { x: nodes[visibilityId].x + 180, y: nodes[visibilityId].y + 150 };
            
            nodes[visibilityListId] = { 
              id: visibilityListId, 
              label: `${visibility} Workspaces`, 
              items, 
              x: pos.x, 
              y: pos.y, 
              color: getWorkspaceVisibilityColor(visibility), 
              connections: [], 
              width: listWidth, 
              height: listHeight 
            };
          }
        });
      }

      // Roles group
      if (roles.length > 0) {
        const roleGroups = groupRolesByScope(roles);

        nodes['roles'] = {
          id: 'roles',
          label: `Roles\n${roles.length} total`,
          x: 200,
          y: 450,
          color: '#8b5cf6',
          connections: Object.keys(roleGroups).map(scope => `role-${toSlug(scope)}`)
        };

        let roleYOffset = 530;
        Object.entries(roleGroups).forEach(([scope, scopeRoles]) => {
          const roleId = `role-${toSlug(scope)}`;
          const roleListId = `${roleId}-list`;
          
          nodes[roleId] = {
            id: roleId,
            label: `${scope}\n${scopeRoles.length} roles`,
            x: 50,
            y: roleYOffset,
            color: getRoleColor(scope),
            connections: [roleListId]
          };
          roleYOffset += 80;

          if (expandedNodes.has(roleId) && scopeRoles.length > 0) {
            const items = createRoleItems(scopeRoles);
            const listWidth = 520;
            const lineHeight = 16;
            const padding = 24;
            const listHeight = padding + lineHeight * Math.max(1, Math.ceil(items.length / 2)) * 2;
            const pos = { x: nodes[roleId].x + 180, y: nodes[roleId].y + 150 };
            
            nodes[roleListId] = { 
              id: roleListId, 
              label: `${scope} Roles`, 
              items, 
              x: pos.x, 
              y: pos.y, 
              color: getRoleColor(scope), 
              connections: [], 
              width: listWidth, 
              height: listHeight 
            };
          }
        });
      }
    }

    // Add HuggingFace scope-based groups if present
    if ((normalized.models || []).length > 0 && (normalized.provider || '').toLowerCase() === 'huggingface') {
      const scopeStats = getHuggingFaceScopeStats(normalized.models);

      // Main scopes node
      nodes['scopes'] = {
        id: 'scopes',
        label: `Model Permissions\n${normalized.total_models || 0} models`,
        x: 600,
        y: 200,
        color: '#8b5cf6',
        connections: ['hf-read', 'hf-write', 'hf-private']
      };
      nodes['github-token'].connections.push('scopes');

      // Read scope group
      nodes['hf-read'] = {
        id: 'hf-read',
        label: `Read Access\n${scopeStats.read.true + scopeStats.read.false} total`,
        x: 400,
        y: 320,
        color: '#10b981',
        connections: ['hf-read-true', 'hf-read-false']
      };

      // Read true/false nodes
      nodes['hf-read-true'] = {
        id: 'hf-read-true',
        label: `${getScopeDisplayLabel('read', true)}\n${scopeStats.read.true} models`,
        x: 200,
        y: 400,
        color: getScopeColor('read', true),
        connections: scopeStats.read.true > 0 ? ['hf-read-true-list'] : []
      };

      nodes['hf-read-false'] = {
        id: 'hf-read-false',
        label: `${getScopeDisplayLabel('read', false)}\n${scopeStats.read.false} models`,
        x: 200,
        y: 500,
        color: getScopeColor('read', false),
        connections: scopeStats.read.false > 0 ? ['hf-read-false-list'] : []
      };

      // Write scope group
      nodes['hf-write'] = {
        id: 'hf-write',
        label: `Write Access\n${scopeStats.write.true + scopeStats.write.false} total`,
        x: 600,
        y: 320,
        color: '#3b82f6',
        connections: ['hf-write-true', 'hf-write-false']
      };

      // Write true/false nodes
      nodes['hf-write-true'] = {
        id: 'hf-write-true',
        label: `${getScopeDisplayLabel('write', true)}\n${scopeStats.write.true} models`,
        x: 500,
        y: 400,
        color: getScopeColor('write', true),
        connections: scopeStats.write.true > 0 ? ['hf-write-true-list'] : []
      };

      nodes['hf-write-false'] = {
        id: 'hf-write-false',
        label: `${getScopeDisplayLabel('write', false)}\n${scopeStats.write.false} models`,
        x: 500,
        y: 500,
        color: getScopeColor('write', false),
        connections: scopeStats.write.false > 0 ? ['hf-write-false-list'] : []
      };

      // Private scope group
      nodes['hf-private'] = {
        id: 'hf-private',
        label: `Private Models\n${scopeStats.private.true + scopeStats.private.false} total`,
        x: 800,
        y: 320,
        color: '#8b5cf6',
        connections: ['hf-private-true', 'hf-private-false']
      };

      // Private true/false nodes
      nodes['hf-private-true'] = {
        id: 'hf-private-true',
        label: `${getScopeDisplayLabel('private', true)}\n${scopeStats.private.true} models`,
        x: 700,
        y: 400,
        color: getScopeColor('private', true),
        connections: scopeStats.private.true > 0 ? ['hf-private-true-list'] : []
      };

      nodes['hf-private-false'] = {
        id: 'hf-private-false',
        label: `${getScopeDisplayLabel('private', false)}\n${scopeStats.private.false} models`,
        x: 700,
        y: 500,
        color: getScopeColor('private', false),
        connections: scopeStats.private.false > 0 ? ['hf-private-false-list'] : []
      };

    }

    // Add Resources group if present (Anthropic)
    if ((normalized.resources || []).length > 0) {
      nodes['resources'] = {
        id: 'resources',
        label: `Resources\n${normalized.resources.length} total`,
        x: 750,
        y: 340,
        color: '#06b6d4',
        connections: ['resources-list']
      };
      nodes['github-token'].connections.push('resources');

      // On expand, show a single unified list node containing all resources with details
      if (expandedNodes.has('resources')) {
        const items = (normalized.resources || []).map((r) => ({
          name: `${r.name || r.id} — ${r.type || 'resource'} — ${r.id || ''}`.trim(),
        }));
        const listId = 'resources-list';
        // Make the container larger to accommodate multi-line entries in two columns
        const listWidth = 680; // wider list box
        const lineHeight = 18; // slightly taller line height for readability
        const padding = 32; // a bit more padding
        const rows = Math.max(1, Math.ceil(items.length / 2));
        const assumedLinesPerItem = 3; // account for wrap due to long IDs
        const listHeight = padding + rows * lineHeight * assumedLinesPerItem;
        const pos = { x: nodes['resources'].x + 180, y: nodes['resources'].y + 150 };
        nodes[listId] = { id: listId, label: `All Resources`, items, x: pos.x, y: pos.y, color: '#99f6e4', connections: [], width: listWidth, height: listHeight };
      }
    }

    // Add S3 Buckets group if present (AWS)
    if ((normalized.affected_buckets || []).length > 0 && (normalized.provider || '').toLowerCase() === 'aws') {
      const buckets = normalized.affected_buckets || [];
      
      nodes['buckets'] = {
        id: 'buckets',
        label: `Affected Buckets\n${normalized.total_buckets || buckets.length} total`,
        x: 600,
        y: 200,
        color: '#10b981',
        connections: ['buckets-list']
      };
      nodes['github-token'].connections.push('buckets');

      // On expand, show a single unified list node containing all buckets with details
      if (expandedNodes.has('buckets')) {
        const items = buckets.map((b) => ({
          name: `${b.name || b.bucket_name || 'Unknown'} (${b.region || 'N/A'})\n${b.permission_level || 'N/A'}`,
          url: b.url || `s3://${b.name || b.bucket_name || ''}`,
        }));
        const listId = 'buckets-list';
        const listWidth = 520;
        const lineHeight = 18;
        const padding = 24;
        const rows = Math.max(1, Math.ceil(items.length / 2));
        const listHeight = padding + rows * lineHeight * 2;
        const pos = { x: nodes['buckets'].x + 180, y: nodes['buckets'].y + 150 };
        nodes[listId] = { 
          id: listId, 
          label: `All Affected Buckets`, 
          items, 
          x: pos.x, 
          y: pos.y, 
          color: '#93c5fd', 
          connections: [], 
          width: listWidth, 
          height: listHeight 
        };
      }
    }

    // Add AWS Services group if present (already handled as scopes, but ensure connection)
    if ((normalized.scopes || []).length > 0 && (normalized.provider || '').toLowerCase() === 'aws') {
      if (!nodes['scopes']) {
        nodes['scopes'] = {
          id: 'scopes',
          label: `AWS Services\n${normalized.total_services || normalized.total_scopes || 0} services`,
          x: 600,
          y: 400,
          color: '#8b5cf6',
          connections: ['scopes-in-scope', 'scopes-actions']
        };
        nodes['github-token'].connections.push('scopes');
      }
    }

    // Add IAM Permissions group if present (AWS)
    if ((normalized.iam_permissions || []).length > 0 && (normalized.provider || '').toLowerCase() === 'aws') {
      const permissions = normalized.iam_permissions || [];
      
      nodes['iam-permissions'] = {
        id: 'iam-permissions',
        label: `IAM Permissions\n${normalized.total_permissions || permissions.length} permissions`,
        x: 200,
        y: 450,
        color: '#f59e0b',
        connections: ['iam-permissions-list']
      };
      nodes['github-token'].connections.push('iam-permissions');

      // On expand, show a single unified list node containing all permissions
      if (expandedNodes.has('iam-permissions')) {
        const items = permissions.map((p) => ({
          name: `${p.permission || p.action || 'Unknown'} - ${p.effect || 'Allow'}\n${Array.isArray(p.resources) ? p.resources.join(', ') : (p.resources || '*')}`,
        }));
        const listId = 'iam-permissions-list';
        const listWidth = 520;
        const lineHeight = 18;
        const padding = 24;
        const rows = Math.max(1, Math.ceil(items.length / 2));
        const listHeight = padding + rows * lineHeight * 2;
        const pos = { x: nodes['iam-permissions'].x + 180, y: nodes['iam-permissions'].y + 150 };
        nodes[listId] = { 
          id: listId, 
          label: `All IAM Permissions`, 
          items, 
          x: pos.x, 
          y: pos.y, 
          color: '#fde68a', 
          connections: [], 
          width: listWidth, 
          height: listHeight 
        };
      }
    }

    // Old repository category nodes removed in favor of Visibility and Ownership grouping

    // Old scope category nodes removed in favor of In-Scope and Action grouping

    return nodes;
}